import { useState } from 'react'

export interface UpdateProfileRequest {
  name: string
}

export interface UpdateProfileResponse {
  message: string
  user: {
    id: string
    email: string
    name: string
    role: string
    emailVerified: boolean
  }
}

export interface UseUpdateProfileResult {
  updateProfile: (data: UpdateProfileRequest) => Promise<UpdateProfileResponse | null>
  loading: boolean
  error: string | null
}

export function useUpdateProfile(): UseUpdateProfileResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateProfile = async (data: UpdateProfileRequest): Promise<UpdateProfileResponse | null> => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to update profile (${response.status})`)
      }

      const responseData: UpdateProfileResponse = await response.json()
      return responseData
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile'
      setError(errorMessage)
      console.error('Error updating profile:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  return {
    updateProfile,
    loading,
    error
  }
}
